window.openQuickBuyByHandle = (handle) => {
  const fakeBtn = document.createElement('button');
  // QuickbuyDrawerButton.show() fetches this url with ?view=quickbuy. Unprefixed it returns
  // the default-language drawer, so a shopper on /de/ opened an English quick buy. Page routes
  // resolve the canonical handle under a locale prefix, so this is safe on every storefront.
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  fakeBtn.dataset.productUrl = `${root}products/${handle}`;

  const drawer = document.querySelector('quickbuy-drawer');

  if (!drawer) {
    console.error('Quickbuy drawer not found');
    return;
  }

  Object.setPrototypeOf(fakeBtn, QuickbuyDrawerButton.prototype);

  fakeBtn.drawer = drawer;
  fakeBtn.quickbuyDrawer = drawer.querySelector('.drawer__content');

  fakeBtn.show();
};

class productGroupSwatch extends HTMLElement {

  static prefetchedUrls = new Set();

  // Mouse-triggered preview swap is skipped on devices with no real hover
  // (touch); keyboard focus must still work everywhere, so it's never
  // gated on this.
  static supportsHover = window.matchMedia('(hover: hover)').matches;

  hoverTimer = null;

  connectedCallback() {
    this.context = this.dataset.context || 'pdp';
    this.group = this.closest('product-group');

    if (this.context === 'pdp' || this.context === 'quick-buy') {
      this.addEventListener('mouseenter', this.handlePdpHover);
      this.addEventListener('mouseleave', this.handlePdpLeave);
    }

    if (this.context === 'product-card') {
      // Resolved once - none of these change after the card's initial render,
      // so there's no need to re-query the DOM on every hover/focus event.
      this.card = this.closest('.product-card');
      this.cardImg = this.card?.querySelector('.product-card__image:last-child') || null;
      this.cardTitleEl = this.card?.querySelector('.product-card__heading span') || null;
      // Neither the .price element nor the preview template is cached. Previewing swaps
      // the whole .price node out (see handleCardHover), so the live one changes as
      // sibling swatches take over; and the template may not exist yet at connect time -
      // swatches past the all_products cap get theirs injected later by swatchHydration.
      // Both are looked up on demand instead.

      this.addEventListener('mouseenter', this.handleCardMouseEnter);
      this.addEventListener('mouseleave', this.handleCardMouseLeave);
      this.addEventListener('focusin', this.handleCardHover);
      this.addEventListener('focusout', this.handleCardLeave);
    }

    if (this.context === 'quick-buy') {
      this.addEventListener('click', this.handleQuickBuyClick);
    }

    if (this.context === 'pdp' || this.context === 'product-card') {
      this.addEventListener('mouseenter', this.schedulePrefetch);
      this.addEventListener('mouseleave', this.cancelPrefetch);
      this.addEventListener('touchstart', this.prefetchProduct, { passive: true });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.handlePdpHover);
    this.removeEventListener('mouseleave', this.handlePdpLeave);

    this.removeEventListener('mouseenter', this.handleCardMouseEnter);
    this.removeEventListener('mouseleave', this.handleCardMouseLeave);
    this.removeEventListener('focusin', this.handleCardHover);
    this.removeEventListener('focusout', this.handleCardLeave);

    this.removeEventListener('click', this.handleQuickBuyClick);

    this.removeEventListener('mouseenter', this.schedulePrefetch);
    this.removeEventListener('mouseleave', this.cancelPrefetch);
    this.removeEventListener('touchstart', this.prefetchProduct);
    this.cancelPrefetch();
  }

  // Warms the browser's HTTP cache with the linked product page so the
  // eventual navigation (a real page load, since swatches link to separate
  // products rather than swapping a variant) feels instant.
  schedulePrefetch = () => {
    this.hoverTimer = setTimeout(this.prefetchProduct, 80);
  };

  cancelPrefetch = () => {
    if (this.hoverTimer !== null) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
  };

  prefetchProduct = () => {
    this.hoverTimer = null;

    const url = this.dataset.productUrl;
    if (!url || productGroupSwatch.prefetchedUrls.has(url)) return;
    if (navigator.connection?.saveData) return;

    productGroupSwatch.prefetchedUrls.add(url);

    const link = document.createElement('link');

    if (link.relList?.supports?.('prefetch')) {
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      document.head.appendChild(link);
    } else {
      fetch(url, { priority: 'low' }).catch(() => { });
    }
  };

  handlePdpHover = () => {
    if (window.innerWidth < 768 || !this.group) return;

    const selectedColorTextEl = this.group.querySelector('.js-selected-color');
    if (!selectedColorTextEl) return;

    if (!selectedColorTextEl.dataset.originalText) {
      selectedColorTextEl.dataset.originalText = selectedColorTextEl.innerText;
    }

    selectedColorTextEl.innerText = this.dataset.swatchName;
  };

  handlePdpLeave = () => {
    if (!this.group) return;

    const selectedColorTextEl = this.group.querySelector('.js-selected-color');
    if (!selectedColorTextEl) return;

    if (selectedColorTextEl.dataset.originalText) {
      selectedColorTextEl.innerText = selectedColorTextEl.dataset.originalText;
    }
  };

  handleCardMouseEnter = () => {
    if (!productGroupSwatch.supportsHover) return;
    this.handleCardHover();
  };

  handleCardMouseLeave = () => {
    if (!productGroupSwatch.supportsHover) return;
    this.handleCardLeave();
  };

  handleCardHover = () => {
    if (!this.card) return;

    this.card.classList.add('product-card--swatch-active');

    const previewBase = this.dataset.previewImage;

    // Replace (or add) the width param rather than appending, to avoid duplicates.
    // data-preview-image URLs from Liquid already include ?v=xxx&width=200.
    const withWidth = (url, w) => {
      if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, `$1width=${w}`);
      return url + (url.includes('?') ? '&' : '?') + `width=${w}`;
    };

    if (previewBase && this.cardImg) {
      const img = this.cardImg;

      if (!img.dataset.originalSrc) {
        img.dataset.originalSrc = img.src;
        img.dataset.originalSrcset = img.srcset;
      }

      img.src = withWidth(previewBase, 600);

      const widths = [200, 300, 400, 500, 600];
      img.srcset = widths
        .map(w => `${withWidth(previewBase, w)} ${w}w`)
        .join(', ');
    }

    if (this.cardTitleEl && this.dataset.productTitle) {
      if (!this.cardTitleEl.dataset.originalText) {
        this.cardTitleEl.dataset.originalText = this.cardTitleEl.textContent;
      }
      this.cardTitleEl.textContent = this.dataset.productTitle;
    }

    const priceTemplate = this.querySelector('template.product-group-swatch__price-preview');

    if (priceTemplate) {
      // Swap the entire .price element, not just its contents: price--on-sale /
      // price--sold-out live on that wrapper and every rule that decides whether
      // the regular or sale block shows is a descendant selector off it.
      const liveEl = this.card.querySelector('.price');
      const previewEl = priceTemplate.content.querySelector('.price');

      if (liveEl && previewEl) {
        // Stored on the card, not the swatch: siblings share one price node.
        if (!this.card.originalPriceEl) this.card.originalPriceEl = liveEl;
        liveEl.replaceWith(previewEl.cloneNode(true));
      }
    }

    if (previewBase) {
      this.card.style.setProperty('--swatch-preview-image', `url("${withWidth(previewBase, 600)}")`);
    }
  };

  handleCardLeave = () => {
    if (!this.card) return;

    this.card.classList.remove('product-card--swatch-active');

    if (this.cardImg?.dataset.originalSrc) {
      this.cardImg.src = this.cardImg.dataset.originalSrc;
      this.cardImg.srcset = this.cardImg.dataset.originalSrcset;
    }

    if (this.cardTitleEl?.dataset.originalText) {
      this.cardTitleEl.textContent = this.cardTitleEl.dataset.originalText;
    }

    this.card.style.removeProperty('--swatch-preview-image');

    const originalPriceEl = this.card.originalPriceEl;
    if (originalPriceEl) {
      const liveEl = this.card.querySelector('.price');
      if (liveEl && liveEl !== originalPriceEl) liveEl.replaceWith(originalPriceEl);
    }
  };

  handleQuickBuyClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const handle = this.dataset.productHandle;
    if (!handle) return;

    openQuickBuyByHandle(handle);
  };
}

customElements.define('product-group-swatch', productGroupSwatch);

// Hide any swatch that is partially clipped by the overflow:hidden swatch-list
function updatePdpSwatchVisibility(list) {
  const swatches = list.querySelectorAll(':scope > product-group-swatch');
  // Reset first so we re-evaluate after a resize
  swatches.forEach(swatch => { swatch.style.visibility = ''; });
  const listRight = list.getBoundingClientRect().right;
  let visibleCount = 0;
  swatches.forEach(swatch => {
    if (swatch.getBoundingClientRect().right > listRight + 1) {
      swatch.style.visibility = 'hidden';
    } else {
      visibleCount++;
    }
  });

  const validTotal = parseInt(list.dataset.validTotal, 10);
  const renderedCount = swatches.length;
  const moreOptionsBtn = list.closest('.product-form__input.product-group')
    ?.querySelector('more-options-drawer-button');
  if (moreOptionsBtn && !isNaN(validTotal)) {
    const needsButton = visibleCount < renderedCount || renderedCount < validTotal;
    moreOptionsBtn.style.display = needsButton ? '' : 'none';
  }
}

document.querySelectorAll('.product-group--pdp .product-group__swatch-list').forEach(list => {
  // Defer until layout is painted so getBoundingClientRect is accurate
  requestAnimationFrame(() => updatePdpSwatchVisibility(list));
  new ResizeObserver(() => updatePdpSwatchVisibility(list)).observe(list);
});

// Card swatch strips are a nowrap flex row clipped by overflow:hidden, so how many swatches
// fit is a function of the rendered card width, not a breakpoint. Show every swatch that fits
// whole and roll the rest into the "+N" label, which is sized to its text, not to a swatch.
function updateCardSwatchVisibility(list) {
  const more = list.querySelector(':scope > .product-group__more-options');
  const swatches = Array.from(list.querySelectorAll(':scope > product-group-swatch'));
  if (!more || !swatches.length) return;

  // Reset first so a resize re-evaluates against the full set rather than the last result.
  swatches.forEach(swatch => { swatch.style.display = ''; });

  // Only swatches that will actually paint get a slot. A pending stub is display:none until
  // hydration fills it, so it takes no width
  const paintable = swatches.filter(swatch => !swatch.classList.contains('product-group-swatch--pending'));
  if (!paintable.length) return;

  // Stubs are pinned shut for now so that one hydrating mid-pass cannot appear past the cut
  // before the re-run lays the row out again.
  swatches.forEach(swatch => {
    if (!paintable.includes(swatch)) swatch.style.display = 'none';
  });

  const width = list.clientWidth;
  const itemWidth = paintable.reduce((max, swatch) => Math.max(max, swatch.getBoundingClientRect().width), 0);
  if (!width || !itemWidth) return;

  const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
  const total = parseInt(more.dataset.total, 10) || swatches.length;
  const countEl = more.querySelector('.product-group__more-options-count');

  // Painted width is the only cap. There is no editorial swatch limit on cards: Liquid
  // renders the whole group, so every swatch that fits is shown and the rest roll into "+N".
  //
  // 1px of slack throughout: fractional card widths otherwise drop the last slot that
  // visually fits.
  const count = paintable.length;

  // Everything present fits and there is nothing left to report, so the label is not needed
  // and its width is not worth spending.
  let shown = count;
  let remaining = total - count;
  const fitsWithoutLabel = remaining < 1
    && count * itemWidth + (count - 1) * gap <= width + 1;

  if (!fitsWithoutLabel) {
    // Count and label width depend on each other - one more swatch means one fewer to report,
    // and a smaller number is a narrower label - so solve them together rather than reserving
    // a fixed upper bound.
    more.hidden = false;

    const fitsWithLabel = (k) => {
      countEl.textContent = `+${Math.max(0, total - k)}`;
      return k * (itemWidth + gap) + more.getBoundingClientRect().width <= width + 1;
    };

    shown = Math.max(1, Math.min(count, Math.floor((width + 1) / (itemWidth + gap))));
    while (shown > 1 && !fitsWithLabel(shown)) shown--;
    remaining = total - shown;
  }

  paintable.forEach((swatch, index) => {
    swatch.style.display = index < shown ? '' : 'none';
  });

  more.hidden = remaining < 1;
  // Always written, never left holding the measuring value from above.
  countEl.textContent = remaining > 0 ? `+${remaining}` : '';
}

const observedCardSwatchRows = new WeakSet();

function observeCardSwatchRows(root = document) {
  const rows = new Set();
  if (root.matches && root.matches('.product-group--product-card')) rows.add(root);
  root.querySelectorAll('.product-group--product-card').forEach(row => rows.add(row));

  rows.forEach(row => {
    if (observedCardSwatchRows.has(row)) return;
    observedCardSwatchRows.add(row);
    requestAnimationFrame(() => updateCardSwatchVisibility(row));
    new ResizeObserver(() => updateCardSwatchVisibility(row)).observe(row);

    // The row is only revealed on hover/focus, and the card pads its details block in that
    // state - so the resting measurement is a slot or so out from what the shopper sees.
    const card = row.closest('.product-card');
    if (!card) return;
    const remeasure = () => updateCardSwatchVisibility(row);
    card.addEventListener('mouseenter', remeasure);
    card.addEventListener('focusin', remeasure);
  });
}

// Prevent the grid row from shrinking when product-cards with swatches are hovered.
if (window.matchMedia('(hover: hover)').matches) {
  const cardSelector = '.product-card.product-card--has-swatches:not(.product-card--no-hover)';
  const hoveredCards = new WeakSet();

  document.addEventListener('mouseover', (e) => {
    const card = e.target.closest(cardSelector);
    if (!card || hoveredCards.has(card)) return;
    hoveredCards.add(card);
    card.style.minHeight = card.getBoundingClientRect().height + 'px';
    card.classList.add('product-card--is-hovered');
  });

  document.addEventListener('mouseout', (e) => {
    const card = e.target.closest(cardSelector);
    if (!card || card.contains(e.relatedTarget)) return;
    hoveredCards.delete(card);
    card.classList.remove('product-card--is-hovered');
    card.style.minHeight = '';
  });
}

// Swatches whose product couldn't be resolved server-side render as pending stubs. Two
// reasons: all_products caps at 20 unique lookups per page render and product groups exceed
// that - a collection page spends the whole budget on its first card or two - and cards
// deliberately stop resolving past their display limit while still rendering the rest of the
// group, so that drafts can't eat swatch slots (see 'ua_product-group').
//
// Cards render the whole group, so every member is in the DOM and the removals below make
// the "+N" badge and the "N options" text exact rather than approximate.
//
// They're filled in from /collections/all/<subcollection tag>?view=ua_swatch-data. Every
// grouped product carries exactly one 'subcollection:' tag and that tag collection is a
// superset of the group, so one request covers a whole group
(function swatchHydration() {
  const FRAGMENT_MAX_BYTES = 512 * 1024;
  const FRAGMENT_TIMEOUT_MS = 10000;
  // A tag Shopify doesn't recognise isn't an error - /collections/all/<nonsense> silently
  // serves the whole catalogue (5846 products, 117 pages when measured). A subcollection is
  // a handful of colourways, so a large count means the tag was ignored: treat the response
  // as a miss and let the per-product fallback handle it
  const MAX_SUBCOLLECTION_PRODUCTS = 200;
  const MAX_FRAGMENT_PAGES = 4;
  const PENDING_SELECTOR = 'product-group-swatch.product-group-swatch--pending';

  // Both caches are page-wide: two cards in the same subcollection share one request, and a
  // handle appearing in several groups is only ever resolved once.
  const fragmentCache = new Map(); // subcollection handle -> Promise<Map<reporting mid, data>>
  const handleCache = new Map(); // canonical product handle -> Promise<data | 'gone' | null>

  const processedGroups = new WeakSet();
  const cardPending = new WeakMap();

  function revealCount(card) {
    if (!card) return;
    const countSpan = card.querySelector('.product-group__options-count');
    if (countSpan) countSpan.style.visibility = '';
  }

  function settle(card, count = 1) {
    if (!card || !cardPending.has(card)) return;
    const remaining = cardPending.get(card) - count;
    cardPending.set(card, remaining);
    if (remaining <= 0) revealCount(card);
  }

  function updateOptionsCount(card, removedCount = 0) {
    if (!card) return;
    const countSpan = card.querySelector('.product-group__options-count');
    if (!countSpan) return;

    const remaining = Math.max(0, parseInt(countSpan.dataset.total, 10) - removedCount);
    countSpan.dataset.total = remaining;

    if (remaining <= 1) {
      countSpan.remove();
      // With ≤1 swatch left, strip the hover treatment from the card entirely.
      card.classList.remove('product-card--has-swatches');
    } else {
      countSpan.textContent = countSpan.textContent.replace(/\d+/, String(remaining));
    }
  }

  // PDP truncation is measured against painted widths, so it has to be redone once swatches
  // that weren't there at first paint have appeared.
  function refreshPdpVisibility(group) {
    group.querySelectorAll('.product-group__swatch-list').forEach(list => {
      // data-valid-total is the deduped group size from the metafield, which can't tell a
      // draft from a member past the all_products cap - so it counts drafts that hydration
      // has since removed. Left stale it keeps renderedCount below validTotal permanently,
      // and More Options stays visible with nothing behind it.
      list.dataset.validTotal = list.querySelectorAll(':scope > product-group-swatch').length;
      requestAnimationFrame(() => updatePdpSwatchVisibility(list));
    });
  }

  // Hydration removes swatches whose product is gone, so the group's real total drops and the
  // "+N" square has to be re-measured against what's actually left.
  function refreshCardSwatchVisibility(group, removedCount) {
    group.querySelectorAll('.product-group--product-card').forEach(list => {
      const more = list.querySelector(':scope > .product-group__more-options');
      if (more && removedCount) {
        const total = (parseInt(more.dataset.total, 10) || 0) - removedCount;
        more.dataset.total = Math.max(0, total);
      }
      requestAnimationFrame(() => updateCardSwatchVisibility(list));
    });
  }

  function parseFragment(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const items = new Map();

    doc.querySelectorAll('swatch-data[data-mid]').forEach(node => {
      let images = [];
      const json = node.querySelector('script.swatch-data__images');
      if (json) {
        try {
          images = JSON.parse(json.textContent) || [];
        } catch (e) {
          images = [];
        }
      }

      items.set(node.dataset.mid, {
        title: node.dataset.productTitle,
        available: node.dataset.available === 'true',
        images,
        priceEl: node.querySelector('.swatch-data__price > .price'),
      });
    });

    const group = doc.querySelector('swatch-group');
    const pages = parseInt(group?.dataset.pages, 10);
    const count = parseInt(group?.dataset.count, 10);

    return {
      items,
      // A missing data-pages means an unexpected response shape, so assume one page. A
      // declared 0 is the fragment's own "tag not recognised" signal and must stay 0 -
      // coercing it to 1 would make the size guard depend on the count guard running first.
      pages: Number.isNaN(pages) ? 1 : pages,
      count: Number.isNaN(count) ? items.size : count,
    };
  }

  // '/' on the default locale, '/de/' etc. elsewhere. The fragment is fetched through it so its
  // titles and prices come back in the visitor's language - matching is on the mid, which
  // doesn't change per locale, so the translated handles in the response don't matter.
  const localeRoot = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

  function fetchFragmentPage(subcollection, page) {
    const url = `${localeRoot}collections/all/${encodeURIComponent(subcollection)}?view=ua_swatch-data`
      + (page > 1 ? `&page=${page}` : '');

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FRAGMENT_TIMEOUT_MS);

    return fetch(url, { priority: 'low', signal: controller.signal })
      .then(res => {
        if (!res.ok) return null;

        // An unresolved ?view= isn't an error to Shopify - it serves the entire collection
        // page (~1.4MB) with a 200. Check the declared size before reading the body, then
        // the real size and a sentinel, rather than handing that to DOMParser.
        const declared = parseInt(res.headers.get('content-length'), 10);
        if (declared > FRAGMENT_MAX_BYTES) return null;

        return res.text();
      })
      .then(html => {
        if (!html) return null;
        if (html.length > FRAGMENT_MAX_BYTES) return null;
        if (!html.includes('<swatch-group')) return null;
        return html;
      })
      .catch(() => null)
      .finally(() => clearTimeout(timer));
  }

  function loadSubcollection(subcollection) {
    if (fragmentCache.has(subcollection)) return fragmentCache.get(subcollection);

    // Returns { items, authoritative }: items maps reporting mid -> data (as before);
    // authoritative is true only when the fragment loaded cleanly and covered the whole
    // subcollection. When true, absence of a mid means the product is genuinely not on
    // this storefront (draft, archived, deleted, market-hidden) and its swatch can be
    // removed without probing /products/<handle>.js - which is what removes the 404 chatter
    // that was showing up as a "failed request" signal in monitoring.
    //
    // When false (fragment fetch failed, tag over the 200-product cap, or pagination cut
    // short by a mid-way failure) absence is ambiguous and we keep the per-handle probe
    // as the fallback for that group's unresolved entries. Losing this distinction would
    // let a single 5xx / 429 on the fragment endpoint silently delete every swatch in an
    // affected subcollection.
    const request = fetchFragmentPage(subcollection, 1).then(async html => {
      if (!html) return { items: new Map(), authoritative: false };

      const { items, pages, count } = parseFragment(html);

      if (count > MAX_SUBCOLLECTION_PRODUCTS) return { items: new Map(), authoritative: false };

      // Only reachable past Shopify's 50-per-page maximum, which no observed subcollection
      // approaches. Kept so that a larger one degrades to slow rather than to wrong.
      const lastPage = Math.min(pages, MAX_FRAGMENT_PAGES);
      let complete = true;
      for (let page = 2; page <= lastPage; page++) {
        const next = await fetchFragmentPage(subcollection, page);
        if (!next) { complete = false; break; }
        parseFragment(next).items.forEach((value, key) => items.set(key, value));
      }

      return { items, authoritative: complete };
    });

    fragmentCache.set(subcollection, request);
    return request;
  }

  // The per-product fallback, used for anything the fragment didn't authoritatively cover: a
  // group with no subcollection tag, a request that tripped one of the guards above, or a
  // paginated fragment cut short by a mid-way failure. It yields an image but no price,
  // which is what this file did for every swatch before.
  //
  // It still recognises 404 as "gone" here, but authoritative fragment absence handles the
  // draft/archived/market-hidden cases up front - so this path is only reached for the
  // narrow set of groups where absence is genuinely ambiguous. That's what removes the
  // per-missing-product 404 traffic without giving up the safety net.
  //
  // Deliberately NOT prefixed with localeRoot. The handle here comes from the group metafield,
  // which stores canonical (default-locale) handles, and those only resolve unprefixed:
  // /products/<canonical>.js is a 200 on every locale, /de/products/<canonical>.js is a 404.
  // Prefixing this would 404 every swatch on every localized page - and a 404 is what tells
  // us a product is gone, so it would delete the entire swatch row.
  function loadHandle(handle) {
    if (handleCache.has(handle)) return handleCache.get(handle);

    const sized = url => {
      const base = url.startsWith('//') ? `https:${url}` : url;
      return base + (base.includes('?') ? '&' : '?') + 'width=200';
    };

    const request = fetch(`/products/${handle}.js`, { priority: 'low' })
      .then(res => {
        if (res.status === 404) return 'gone';
        return res.ok ? res.json() : null;
      })
      .then(async product => {
        if (product === 'gone' || !product) return product === 'gone' ? 'gone' : null;

        return {
          title: product.title,
          available: !!product.available,
          images: (product.images || []).map(sized),
          featuredImage: product.featured_image ? sized(product.featured_image) : null,
          priceEl: await loadCardPrice(handle),
        };
      })
      .catch(() => null);

    handleCache.set(handle, request);
    return request;
  }

  function loadCardPrice(handle) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FRAGMENT_TIMEOUT_MS);

    return fetch(`${localeRoot}products/${encodeURIComponent(handle)}?view=card-product--swatches`, {
      priority: 'low',
      signal: controller.signal,
    })
      .then(res => (res.ok ? res.text() : null))
      .then(html => {
        if (!html || html.length > FRAGMENT_MAX_BYTES) return null;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelector('.product-card .price');
      })
      .catch(() => null)
      .finally(() => clearTimeout(timer));
  }

  // Mirrors 'ua_swatch-image': the group entry's 1-based imageNumber indexes the product's
  // own gallery, falling back to its first image.
  function pickImage(data, imageNumber) {
    const index = parseInt(imageNumber, 10);
    if (index > 1 && data.images[index - 1]) return data.images[index - 1];
    return data.featuredImage || data.images[0] || null;
  }

  function applyPriceTemplate(el, priceEl) {
    if (!priceEl) return;
    if (el.querySelector('template.product-group-swatch__price-preview')) return;

    const price = priceEl.cloneNode(true);

    // The fragment can't know card_minimal - Liquid has no query-param access, and the value
    // varies per card anyway - so it always renders the full price.
    if (el.closest('.card--minimal')) {
      price.querySelectorAll('.price__badge-members-only, .price--loyalty-only--wrapper')
        .forEach(node => node.remove());
    }

    // Inert until handleCardHover swaps it in for the card's own .price element, which is
    // why it goes in a <template> rather than being rendered.
    const template = document.createElement('template');
    template.className = 'product-group-swatch__price-preview';
    template.content.appendChild(price);
    el.prepend(template);
  }

  function applyData(el, data) {
    const card = el.closest('.product-card');
    const imageUrl = pickImage(data, el.dataset.imageNumber);

    if (!el.dataset.productTitle && data.title) el.dataset.productTitle = data.title;

    const anchor = el.querySelector('.product-group--item');
    if (anchor) anchor.classList.toggle('unavailable', !data.available);

    if (imageUrl) {
      if (el.dataset.context === 'product-card' && !el.dataset.previewImage) {
        el.dataset.previewImage = imageUrl;
      }

      const span = el.querySelector('.product-group--item__swatch');
      if (span && !span.style.backgroundImage && !span.dataset.bg) {
        // Cards defer swatch images to first hover (ua_product-card.js reveals data-bg);
        // a card that's already been revealed, and every other context, paints immediately.
        if (card && !card.dataset.swatchImagesLoaded) span.dataset.bg = imageUrl;
        else span.style.backgroundImage = `url("${imageUrl}")`;
      }
    }

    if (el.dataset.context === 'product-card') applyPriceTemplate(el, data.priceEl);

    el.classList.remove('product-group-swatch--pending');
  }

  async function hydrateGroup(group) {
    const pending = Array.from(group.querySelectorAll(PENDING_SELECTOR));
    if (!pending.length) return;

    const card = group.closest('.product-card');
    let removedCount = 0;

    const byHandle = new Map();
    let pendingCount = 0;
    pending.forEach(el => {
      const handle = el.dataset.productHandle;
      if (!handle) {
        // Nothing to look this one up by, so it can never settle. Reveal it rather than
        // leave it hidden forever - it still links to the right product. Same caveat as the
        // transient-failure path below: the server-rendered 'unavailable' class came from a
        // different product, so it can't be trusted once the swatch becomes visible.
        el.querySelector('.product-group--item')?.classList.remove('unavailable');
        el.classList.remove('product-group-swatch--pending');
        return;
      }
      if (!byHandle.has(handle)) byHandle.set(handle, []);
      byHandle.get(handle).push(el);
      pendingCount++;
    });

    // Counted from byHandle, not pending.length: settle() only ever fires for elements that
    // made it into byHandle, so counting the rest would leave the card permanently unsettled
    // and its "+N options" hidden.
    if (card && pendingCount) cardPending.set(card, (cardPending.get(card) || 0) + pendingCount);

    const subcollection = group.dataset.subcollection;
    const fragmentResult = subcollection
      ? await loadSubcollection(subcollection)
      : { items: new Map(), authoritative: false };
    const fragment = fragmentResult.items;

    const unresolved = [];
    byHandle.forEach((els, handle) => {
      // Looked up by mid, not by the handle these elements are grouped under: the fragment is
      // fetched in the visitor's locale so its handles are translated, while these are the
      // canonical ones from the metafield. Grouping stays by handle because that's what the
      // fallback below needs.
      const mid = els[0].dataset.productMid;
      const data = mid ? fragment.get(mid) : null;
      if (data) {
        els.forEach(el => applyData(el, data));
        settle(card, els.length);
      } else if (fragmentResult.authoritative && mid) {
        // Fragment loaded cleanly and this mid isn't in it. The subcollection tag is a
        // superset of the group (every grouped product carries the same tag), so absence
        // from a fully-loaded fragment means the product is draft, archived, deleted, or
        // hidden by Markets from this storefront - the same set of outcomes the per-handle
        // 404 probe used to be the only proof of. Remove without probing to eliminate the
        // 404-per-missing-product traffic that was flagged in monitoring.
        //
        // Only reachable when the fragment is authoritative; the per-handle probe still
        // runs for ambiguous cases (no subcollection tag, fetch failed, tag over cap,
        // pagination cut short) so a single fragment failure can't wipe a whole group.
        els.forEach(el => el.remove());
        removedCount += els.length;
        settle(card, els.length);
      } else {
        unresolved.push([handle, els]);
      }
    });

    await Promise.all(unresolved.map(([handle, els]) => loadHandle(handle).then(data => {
      if (data === 'gone') {
        // A 404 is the only outcome that means the product is really gone (draft, deleted).
        // Drop the swatch rather than leave a blank square linking nowhere; updateOptionsCount
        // below corrects the "+N" count.
        els.forEach(el => el.remove());
        removedCount += els.length;
      } else if (data) {
        els.forEach(el => applyData(el, data));
      } else {
        // Transient: offline, a 5xx, a 429 from hammering /products/<handle>.js, an aborted
        // navigation. The product is almost certainly fine, so keep the swatch and just make
        // it visible again - it still links correctly, it's only missing its image. Removing
        // here would let a rate-limit response silently delete a whole swatch row.
        //
        // The 'unavailable' class has to go with it: Liquid derived it from whatever
        // all_products returned for this stub, which is a different product entirely, so
        // showing the swatch while keeping that class would mark it sold out at random.
        els.forEach(el => {
          el.querySelector('.product-group--item')?.classList.remove('unavailable');
          el.classList.remove('product-group-swatch--pending');
        });
      }
      settle(card, els.length);
    })));

    if (card) updateOptionsCount(card, removedCount);
    refreshPdpVisibility(group);
    refreshCardSwatchVisibility(group, removedCount);
  }

  const nearViewport = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        nearViewport.unobserve(entry.target);
        hydrateGroup(entry.target);
      });
    }, { rootMargin: '400px 0px' })
    : null;

  function register(root) {
    const groups = new Set();
    if (root.matches && root.matches('product-group')) groups.add(root);
    root.querySelectorAll('product-group').forEach(group => groups.add(group));

    groups.forEach(group => {
      if (processedGroups.has(group)) return;
      processedGroups.add(group);

      if (!group.querySelector(PENDING_SELECTOR)) return;

      // Cards wait until they're near the viewport: on a collection page every card has a
      // pending group, and there's no reason to fetch for rows nobody has scrolled to.
      // Everything else - PDP, quick buy, More Options - is a single group already in view.
      const card = group.closest('.product-card');
      if (card && nearViewport) nearViewport.observe(group);
      else hydrateGroup(group);
    });
  }

  // The count is hidden until its card's swatches are settled, so cards with nothing pending
  // have to be revealed explicitly.
  function revealSettledCards(root) {
    const cards = new Set();
    if (root.matches && root.matches('.product-card')) cards.add(root);
    root.querySelectorAll('.product-card').forEach(card => cards.add(card));

    cards.forEach(card => {
      if (!card.querySelector(PENDING_SELECTOR)) revealCount(card);
    });
  }

  function scan(root = document) {
    register(root);
    revealSettledCards(root);
    observeCardSwatchRows(root);
  }

  scan();

  // Quick buy, the More Options drawer and AJAX-paginated grids all inject their markup after
  // load; without this their over-cap swatches would stay pending forever. Batched to one
  // pass per frame, and idempotent via processedGroups.
  let scanScheduled = false;
  new MutationObserver(records => {
    if (scanScheduled) return;

    const addedGroup = records.some(record => Array.from(record.addedNodes).some(
      node => node.nodeType === 1 && (node.matches('product-group') || node.querySelector('product-group'))
    ));
    if (!addedGroup) return;

    scanScheduled = true;
    requestAnimationFrame(() => {
      scanScheduled = false;
      scan();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
